/**
 * Pestaña «General» de la ficha de chasis multinodo en variante tarjetas.
 *
 * Dos columnas como el resto de fichas (FICHA_ESTANDAR_TARJETAS §12.C4):
 *  - izquierda: Identificación (fabricante y modelo con catálogo + alta en
 *    línea, tipo, nº de serie y estado);
 *  - derecha: ubicación y posición en rack apiladas (`sf2-col-stack`).
 *
 * Los campos son los mismos del formulario padre: no hay estado duplicado, se
 * conectan a su `setField`/`handleChange`.
 */
import React from 'react';
import LocationTreeSelect from './common/LocationTreeSelect';
import { CampoTexto, CampoSelect, opcionesDeCatalogo } from './common/Sf2Fields';
import { buildLocationMap, locationPath } from '../utils/locationTree';

export default function ChassisFormSummary({
  form,
  editing = false,
  onFieldChange,
  onChange = null,
  onManufacturerChange = null,
  manufacturerOptions = [],
  modelOptions = [],
  onCreateManufacturer = null,
  onCreateModel = null,
  chassisTypes = [],
  statusOptions = [],
  locations = [],
  racks = [],
  rackName = '',
  onLocationChange,
  onRackChange,
  rangeLabel = '',
  rackHeight = null,
}) {
  const escribir = onChange || ((e) => onFieldChange(e.target.name, e.target.value));
  const opcionesFabricante = opcionesDeCatalogo(manufacturerOptions);
  const opcionesModelo = opcionesDeCatalogo(modelOptions);
  const opcionesTipo = [
    { value: '', label: '— Seleccionar —' },
    ...chassisTypes.map((t) => ({ value: t, label: t })),
  ];
  // Racks de la ubicación elegida; si el rack guardado no está en la lista se
  // añade con su nombre para no perderlo en lectura.
  const opcionesRack = [
    { value: '', label: '— Sin rack —' },
    ...racks.map((r) => ({ value: r.id, label: r.name || r.code })),
  ];
  if (form.rack_id && !opcionesRack.some((o) => String(o.value) === String(form.rack_id)) && rackName) {
    opcionesRack.push({ value: form.rack_id, label: rackName });
  }

  return (
    <div className="sf2-grid sf2-grid--2">
      <div className="sf2-card">
        <h3>Identificación<span className="sf2-count">editable</span></h3>
        <div className="sf2-fields">
          <CampoSelect
            label="Fabricante"
            name="manufacturer"
            value={form.manufacturer}
            onChange={onManufacturerChange
              ? (e) => onManufacturerChange(e.target.value)
              : escribir}
            editing={editing}
            options={opcionesFabricante}
            onAdd={onCreateManufacturer}
            addPlaceholder="Nuevo fabricante"
            addLabel="Añadir fabricante al catálogo"
          />
          <CampoSelect
            label="Modelo"
            name="model"
            value={form.model}
            onChange={escribir}
            editing={editing}
            options={opcionesModelo}
            onAdd={onCreateModel}
            addPlaceholder="Nuevo modelo"
            addLabel="Añadir modelo al catálogo"
          />
          <CampoSelect
            label="Tipo de chasis"
            name="chassis_type"
            value={form.chassis_type}
            onChange={escribir}
            editing={editing}
            options={opcionesTipo}
          />
          <CampoTexto
            label="Nº de serie"
            name="serial_number"
            value={form.serial_number}
            onChange={escribir}
            editing={editing}
            placeholder="SN123456789"
          />
          <CampoSelect
            label="Estado"
            name="status"
            value={form.status}
            onChange={escribir}
            editing={editing}
            options={statusOptions}
          />
        </div>
      </div>

      <div className="sf2-card">
        <h3>Ubicación<span className="sf2-count">editable</span></h3>
        <div className="sf2-fields">
          <div className="sf2-field">
            <label>Ubicación</label>
            {editing ? (
              <LocationTreeSelect
                locations={locations}
                value={form.location_id}
                onChange={(id) => onLocationChange(id)}
                placeholder="— Sin ubicación —"
              />
            ) : (
              <div className="v">
                {form.location_id
                  ? locationPath(buildLocationMap(locations), form.location_id) || '—'
                  : '—'}
              </div>
            )}
          </div>
          <CampoSelect
            label="Rack"
            name="rack_id"
            value={form.rack_id}
            onChange={onRackChange}
            editing={editing}
            options={opcionesRack}
          />
          <CampoTexto
            label="Posición U"
            name="position_u"
            value={form.position_u}
            onChange={escribir}
            editing={editing}
            placeholder="12"
          />
          <CampoTexto
            label="Tamaño U"
            name="size_u"
            value={form.size_u}
            onChange={escribir}
            editing={editing}
            placeholder="2"
          />
        </div>
        {rangeLabel && (
          <div className="sf2-hint" style={{ marginTop: 10 }}>
            Ocupa {rangeLabel} del rack{rackHeight ? ` (${rackHeight}U en total)` : ''}.
          </div>
        )}
      </div>
    </div>
  );
}
